'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { WholeWord, PlusCircle } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface FloatingSuggestButtonProps {
  onClick: () => void;
  className?: string;
}

export const FloatingSuggestButton: React.FC<FloatingSuggestButtonProps> = ({ onClick, className }) => {
  return (
    <div className={cn("relative flex items-center group", className)}>
      <div className="relative cursor-pointer" onClick={onClick}>
        {/* Glow effect matching the theme */}
        <div className="absolute -inset-2 bg-amber-500/20 rounded-full blur-xl group-hover:bg-amber-500/30 transition-all opacity-0 group-hover:opacity-100" />
        
        {/* Main Icon Button - following RulesTriggerIcon pattern */}
        <div 
          className="p-2 md:p-3 bg-amber-400 doodle-border-soft transition-all hover:scale-110 active:scale-95 relative group/btn"
          style={{ transform: 'rotate(1.5deg)' }}
        >
          <WholeWord 
            className="w-5 h-5 md:w-8 md:h-8 text-white stroke-[3px] transition-transform duration-300 group-hover/btn:-rotate-12 group-hover/btn:scale-110" 
          />
          
          {/* Small Plus indicator with pulse animation - Now in Sky Blue for contrast */}
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 2,
              ease: "easeInOut"
            }}
            className="absolute -top-1.5 -right-1.5 w-4 h-4 md:w-5 md:h-5 bg-sky-400 border-2 border-white rounded-full flex items-center justify-center shadow-sm group-hover/btn:bg-sky-500 transition-colors"
          >
            <PlusCircle className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
          </motion.div>
        </div>

        {/* Tooltip on the LEFT side */}
        <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-end bg-card doodle-border-soft p-2.5 shadow-doodle-sm opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all pointer-events-none whitespace-nowrap min-w-max border-2 border-amber-100">
          <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest handwritten leading-none mb-1">SHTO NË FJALOR</span>
          <span className="text-[9px] font-bold text-slate-400 handwritten leading-none">Sugjero fjalë të reja</span>
        </div>
      </div>
    </div>
  );
};
